import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, UntypedFormGroup } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { InputComponent } from "@components/input/input.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { TableComponent } from "@components/table/table.component";
import { Observable, timer } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskColumn } from "~app/common/components/table/models/rask-column";
import { ProduktinstruktionSearchRequestDTO } from "~app/common/models/produkt/ProduktinstruktionSearchRequestDTO";
import { ProduktinstruktionList } from "~app/common/models/produkt/ProduktinstruktionSearchResponseDTO";
import { ProduktspecifikationDTO } from "~app/common/models/produkt/ProduktspecifikationDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { FeatureService } from "~app/common/services/feature.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { Utils } from "~app/common/services/utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ProduktService } from "../../../produkt.service";
import { ProduktDetailTexts } from "../../produkt-detail.texts";

@Component({
    selector: "rask-produkt-detail-produktinstruktioner",
    templateUrl: "./produkt-detail-produktinstruktioner.component.html",
    styleUrls: ["./produkt-detail-produktinstruktioner.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        ListComponent,
        ListColumnDirective,
        RouterLink,
        ButtonComponent,
        InputComponent,
        FormsModule,
        TableComponent
    ]
})
export class ProduktDetailProduktinstruktionerComponent implements OnInit {
    canEdit: boolean;
    form: UntypedFormGroup;
    produktinstruktionList: ProduktinstruktionList[];
    produktinstruktionSearchParams: ProduktinstruktionSearchRequestDTO;
    @Input() produktspecifikation: ProduktspecifikationDTO;
    @Input() rootForm: UntypedFormGroup;
    searchListColumns: RaskColumn[];
    texts = ProduktDetailTexts.produktinstruktionerTab.texts;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly featureService = inject(FeatureService);
    private readonly fieldsToUseInForm = ["produktinstruktioner"];
    private readonly formBinderService = inject(FormBinderService);
    private readonly produktService = inject(ProduktService);

    readonly addProduktinstruktion = (id: number): void => {
        if (this.produktspecifikation.produktinstruktioner.some(p => p.id === id)) {
            return;
        }

        const instruktion = this.produktinstruktionList.find(p => p.id === id);
        this.produktspecifikation.produktinstruktioner.push(instruktion);
        this.produktspecifikation.produktinstruktioner = ObjectUtils.cloneArray(this.produktspecifikation.produktinstruktioner);

        const worker = new Observable(sub => {
            timer(0).subscribe(() => {
                const idx = this.produktinstruktionList.findIndex(p => p.id === id);
                if (idx >= 0) {
                    const temp = ObjectUtils.cloneArray(this.produktinstruktionList);
                    temp.splice(idx, 1);
                    this.produktinstruktionList = temp;
                }

                sub.next(null);
            });
        });

        this.busyService.addWork(worker).subscribe(() => {
            this.form.controls.produktinstruktioner.markAsDirty();
        });
    };

    readonly clearSearchParams = () => {
        this.produktinstruktionSearchParams.mottagningsplatsExternt = "";
        this.produktinstruktionSearchParams.namn = "";
        this.produktinstruktionSearchParams.puid = "";
        this.produktinstruktionSearchParams.sortimentExternt = "";
    };

    downloadPinFile = (puid: string, viol3: boolean): void => {
        this.produktService.downloadPinFile(puid, viol3).subscribe((data) => {
            if (data) {
                const viol3Ext = viol3 ? "V3" : "";
                Utils.download(data, `${puid}${viol3Ext}.pin`);
            }
        });
    };

    ngOnInit(): void {
        this.canEdit = this.authenticationService.canEdit();
        this.produktinstruktionSearchParams = new ProduktinstruktionSearchRequestDTO();
        this.produktinstruktionList = [];
        this.setupForm();
        this.searchListColumns = this.setupColumns();
    }

    readonly searchForProduktinstruktioner = async () => {
        this.busyService.addWork(this.produktService.searchForProduktinstruktion(this.produktinstruktionSearchParams))
            .subscribe((response) => {
                this.produktinstruktionList = response.produktinstruktioner.filter(p => !this.produktspecifikation.produktinstruktioner.some(pp => p.id === pp.id));
            });
    };

    readonly unlinkProduktinstruktion = (id: number): void => {
        const idx = this.produktspecifikation.produktinstruktioner.findIndex(p => p.id === id);
        const removedObject = this.produktspecifikation.produktinstruktioner.splice(idx, 1);
        this.produktspecifikation.produktinstruktioner = ObjectUtils.cloneArray(this.produktspecifikation.produktinstruktioner);

        const temp = ObjectUtils.cloneArray(this.produktinstruktionList);

        const worker = new Observable(sub => {
            timer(0).subscribe(() => {
                if (idx >= 0) {
                    temp.push(...removedObject);
                    this.produktinstruktionList = temp;
                }

                sub.next(null);
            });
        });

        this.busyService.addWork(worker).subscribe(() => {
            this.form.controls.produktinstruktioner.markAsDirty();
        });
    };

    private readonly setupColumns = (): RaskColumn[] => {
        const columns = [
            {
                field: "sortimentExternt",
                filter: true,
                maxWidth: 200,
                headerName: this.texts.sortiment,
                sortable: true,
                type: "span"
            },
            {
                field: "handelssortimentExternt",
                filter: true,
                maxWidth: 200,
                headerName: this.texts.hssortiment,
                sortable: true,
                type: "span"
            },
            {
                field: "namn",
                filter: true,
                headerName: this.texts.namn,
                sortable: true,
                type: "span"
            },
            {
                field: "puid",
                filter: true,
                maxWidth: 200,
                headerName: this.texts.puid,
                sortable: true,
                type: "sref",
                componentOptions: (row) =>
                    ({
                        srefParams: {},
                        sref: `/produktinstruktioner/${row.puid}`
                    })
            },
            {
                field: "mottagningsplatsExternt",
                filter: true,
                maxWidth: 220,
                headerName: this.texts.mottplats,
                sortable: true,
                type: "span"
            },
            {
                field: "mottagningsplatsViol3Externt",
                filter: true,
                maxWidth: 250,
                width: 250,
                headerName: this.texts.mottplatsViol3,
                sortable: true,
                type: "span"
            },
            {
                field: "version",
                filter: true,
                maxWidth: 120,
                headerName: this.texts.version,
                sortable: true,
                type: "span"
            }
        ] as RaskColumn[];

        if (this.canEdit) {
            columns.push({
                field: " ",
                filter: false,
                maxWidth: 110,
                headerName: this.texts.laggTill,
                sortable: false,
                type: "button",
                text: "+",
                onClick: (produktinstruktion: ProduktinstruktionList) => this.addProduktinstruktion(produktinstruktion.id)
            } as RaskColumn);
        }

        return columns;
    };

    private readonly setupForm = (): void => {
        this.form = this.formBinderService.CreateGroup(this.produktspecifikation, this.fieldsToUseInForm);
        this.rootForm.addControl(`produkt-detail-produktinstruktioner`, this.form);
        this.form.markAsPristine();
    };
}
